import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PropertyCard } from "../components/PropertyCard";
import { SectionHeading } from "../components/SectionHeading";
import { Stagger, StaggerItem, Reveal } from "../components/Reveal";
import { getListings, getListingMeta } from "../lib/api";

const PRICE_BANDS = [
  { label: "Any price", value: "any" },
  { label: "Under $4M", value: "0-4000000" },
  { label: "$4M – $6M", value: "4000000-6000000" },
  { label: "$6M – $10M", value: "6000000-10000000" },
  { label: "$10M+", value: "10000000-999000000" },
];

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState({ districts: [], types: [], statuses: [] });
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");

  const filters = useMemo(() => ({
    district: searchParams.get("district") || "any",
    type: searchParams.get("type") || "any",
    status: searchParams.get("status") || "any",
    price: searchParams.get("min_price") ? `${searchParams.get("min_price")}-${searchParams.get("max_price")}` : "any",
    beds: searchParams.get("beds") || "any",
    q: searchParams.get("q") || "",
  }), [searchParams]);

  useEffect(() => {
    getListingMeta().then(setMeta).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.district !== "any") params.district = filters.district;
    if (filters.type !== "any") params.type = filters.type;
    if (filters.status !== "any") params.status = filters.status;
    if (filters.price !== "any") {
      const [min, max] = filters.price.split("-");
      params.min_price = min;
      params.max_price = max;
    }
    if (filters.beds !== "any") params.beds = Number(filters.beds);
    if (filters.q) params.q = filters.q;
    getListings(params)
      .then((r) => { setResults(r.results); setCount(r.count); })
      .catch(() => { setResults([]); setCount(0); })
      .finally(() => setLoading(false));
  }, [filters]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "any" || value === "") {
      if (key === "price") { next.delete("min_price"); next.delete("max_price"); }
      else next.delete(key);
    } else if (key === "price") {
      const [min, max] = value.split("-");
      next.set("min_price", min);
      next.set("max_price", max);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const applyQuery = useCallback(() => setParam("q", query), [query]); // eslint-disable-line

  const reset = () => { setQuery(""); setSearchParams({}, { replace: true }); };

  const selectCls = "h-11 rounded-none border-ivory/20 bg-[#242424] text-sm text-ivory focus:ring-ivory";

  return (
    <main className="pt-[72px]" data-testid="properties-page">
      <section className="border-b border-ivory/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Collection"
            title={<>Landed residences,<br />expertly curated.</>}
            description="Every record below is a clearly-labelled sample demonstrating the listings experience Andy's PA will manage through a CMS in production."
          />
        </div>
      </section>

      <section className="sticky top-[72px] z-30 border-b border-ivory/10 bg-charcoal/90 py-4 backdrop-blur-xl" data-testid="listings-filter-bar">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-6 lg:px-8">
          <Select value={filters.district} onValueChange={(v) => setParam("district", v)}>
            <SelectTrigger data-testid="filter-district" className={`w-44 ${selectCls}`}><SelectValue placeholder="District" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All districts</SelectItem>
              {meta.districts.map((d) => <SelectItem key={d.code} value={d.code}>{d.code} · {d.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.type} onValueChange={(v) => setParam("type", v)}>
            <SelectTrigger data-testid="filter-type" className={`w-40 ${selectCls}`}><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All types</SelectItem>
              {meta.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.price} onValueChange={(v) => setParam("price", v)}>
            <SelectTrigger data-testid="filter-price" className={`w-40 ${selectCls}`}><SelectValue placeholder="Price" /></SelectTrigger>
            <SelectContent>
              {PRICE_BANDS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.beds} onValueChange={(v) => setParam("beds", v)}>
            <SelectTrigger data-testid="filter-beds" className={`w-32 ${selectCls}`}><SelectValue placeholder="Beds" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any beds</SelectItem>
              {[4, 5, 6, 7].map((b) => <SelectItem key={b} value={String(b)}>{b}+ beds</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(v) => setParam("status", v)}>
            <SelectTrigger data-testid="filter-status" className={`w-40 ${selectCls}`}><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All statuses</SelectItem>
              {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="flex min-w-56 flex-1 items-center border border-ivory/20 bg-[#242424]">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyQuery()}
              placeholder="Search estate or road…"
              data-testid="filter-search-input"
              className="h-11 w-full bg-transparent px-4 text-sm text-ivory placeholder:text-ivory/40 focus:outline-none"
            />
            <button type="button" onClick={applyQuery} aria-label="Search listings" data-testid="filter-search-button" className="px-4 text-ivory/60 hover:text-ivory">
              <Search className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={reset}
            data-testid="filter-reset-button"
            className="flex items-center gap-2 px-3 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-ivory"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-ivory/50" data-testid="listings-count">
            {loading ? "Curating…" : `${count} sample residence${count === 1 ? "" : "s"}`}
          </p>

          {!loading && results.length === 0 && (
            <Reveal className="mt-10 border border-ivory/10 bg-[#242424] p-14 text-center" data-testid="listings-empty">
              <p className="font-serif text-3xl text-ivory">Nothing matches that brief — yet.</p>
              <p className="mx-auto mt-3 max-w-md text-sm text-ivory/60">Loosen a filter or two. On the production site, this catalogue will hold Andy's live inventory.</p>
              <button type="button" onClick={reset} data-testid="listings-empty-reset" className="mt-6 bg-brand px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-ivory hover:bg-brand-dark">
                Clear filters
              </button>
            </Reveal>
          )}

          <Stagger className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {results.map((l) => (
              <StaggerItem key={l.id}>
                <PropertyCard listing={l} />
              </StaggerItem>
            ))}
          </Stagger>

          <p className="mt-10 max-w-3xl text-xs leading-relaxed text-ivory/40" data-testid="listings-sample-disclaimer">
            Demonstration notice: every listing, price and specification on this page is a sample record for design
            preview only — not a genuine property offer. In production this catalogue will be managed in the Wix CMS
            by Andy's team, with no page-design edits required to add, update or remove listings.
          </p>
        </div>
      </section>
    </main>
  );
}
